// src/components/PendingBuyRequests/ActionButtonsCell.js

import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Button, CircularProgress, Box } from "@mui/material";
import { BuyRequestStatus } from "../../../../../../constants/enums";
import { selectAllBuyRequests, selectAllSwarms, selectRunEvents } from "../../../../../../store/slices/swarms";
import { useEditBuyRequestMutation, useGetDissaprobalReasonQuery, useLazyGetBuyRequestQuery } from "../../../../../../services/swarmsApi";
import { ReasonMenu } from "../../../../../../components/reasonMenu";
import { useNotification } from '../../../../../../context/NotificationContext';

const ActionButtonsCell = ({ props, setShowError }) => {

  const { showNotification } = useNotification();
  
  const {data: dissaprovalReasons} = useGetDissaprobalReasonQuery();
  const buyRequests = useSelector(selectAllBuyRequests);
  const swarms = useSelector(selectAllSwarms);
  const runEvents = useSelector(selectRunEvents);
  
  const [editBuyRequest] = useEditBuyRequestMutation();
  const [getBuyRequest] = useLazyGetBuyRequestQuery();
  const [loading, setLoading] = useState('');

  const swarmsDict = useMemo(() => {
    const dict = {};
    swarms?.forEach(s => {
      dict[s.id] = s.bteId;
    });
    return dict;
  }, [swarms]);

  const acceptedBuyRequestsDict = useMemo(() => {
    const dict = {};
    buyRequests?.filter(br => br.swarmResolutionTypeId === BuyRequestStatus.Accepted).forEach(br => {
      if (!dict[br.userId]) dict[br.userId] = {};
      dict[br.userId][swarmsDict[br.swarmId]] = true;
    });

    Object.keys(dict).forEach(userId => {
      const userDict = dict[userId];
      Object.keys(userDict).forEach(bteId => {
        runEvents?.forEach(event => {
          if (event.bteId === bteId) {
            event.runEventIds?.forEach(runEventId => {
              userDict[runEventId] = true;
            });
          }
        });
      });
    });

    return dict;
  }, [buyRequests, swarmsDict, runEvents]);

  const haveAlreadyApproved = () => {
    return acceptedBuyRequestsDict[props.row.userId]?.[swarmsDict[props.row.swarmId]];
  };

  const handleBuyClick = async () => {
    try {
      setLoading("BUY");
      await editBuyRequest({
        id: props.row.id,
        swarmResolutionTypeId: BuyRequestStatus.Accepted
      }).unwrap();
      setLoading('');
    } catch (error) {
      setLoading('');
      if (error.status === 401) return; // Error handle by RTK Query Reauth
      if (error.status === 400) {
        showNotification(error.data.message, 'error');
        await getBuyRequest({ query: { swarmResolutionTypeId: BuyRequestStatus.Pending } }).unwrap().catch(e => {
          if (e.status === 401) return; // Error handle by RTK Query Reauth
          throw e;
        });
        return;
      }
      throw error;
    }
  };

  const handlePassClick = async () => {
    try {
      setLoading("PASS");
      await editBuyRequest({
        id: props.row.id,
        swarmResolutionTypeId: BuyRequestStatus.Rejected,
      }).unwrap();
      setLoading('');
    } catch (error) {
      setLoading('');
      if (error.status === 401) return; 
      if (error.status === 400) { 
        showNotification(error.data.message, 'error');
        await getBuyRequest({ query: { swarmResolutionTypeId: BuyRequestStatus.Pending } }).unwrap().catch(e => {
          if (e.status === 401) return; 
          throw e;
        });
        return;
      }
      throw error;
    }
  };

  const handleReasonClick = async (reason) => {
    try {
      setLoading("REASON");
      await editBuyRequest({
        id: props.row.id,
        swarmResolutionTypeId: BuyRequestStatus.Rejected,
        reasonId: reason
      }).unwrap();
      setLoading('');
    }
    catch (error) {
      setLoading('');
      if (error.status === 401) return; 
      if (error.status === 400) { 
        showNotification(error.data.message, 'error');
        await getBuyRequest({ query: { swarmResolutionTypeId: BuyRequestStatus.Pending } }).unwrap().catch(e => {
          if (e.status === 401) return; 
          throw e;
        });
        return;
      }
      throw error;
    }
  };

  const processedStyle = {
    width: 1,
    height: 1,
    backgroundColor: '#BDBDBD',
    color: "white",
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  if (props.row.swarmResolutionTypeId === BuyRequestStatus.Pending) {
    return (
      <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
        <Button
          onClick={handleBuyClick}
          variant="contained"
          color="success"
          sx={{ borderRadius: 1, padding: '4px 6px', minWidth: 50 }}
          disabled={haveAlreadyApproved() || Boolean(loading)}
        >
          {loading === "BUY" ? <CircularProgress size="1.5em" /> : "BUY"}
        </Button>

        <Button
          onClick={handlePassClick}
          disabled={Boolean(loading)}
          variant="contained"
          color="error"
          sx={{ borderRadius: 1, margin: '0 0.25em', padding: '4px 6px', minWidth: 50 }}
        >
          {loading === "PASS" ? <CircularProgress size="1.5em" /> : "PASS"}
        </Button>

        <ReasonMenu reasons={dissaprovalReasons} onSelect={handleReasonClick} loading={loading}>
          <Button
            variant="contained"
            color="error"
            sx={{ borderRadius: 1, padding: '4px 6px', minWidth: 50 }}
            disabled={Boolean(loading)}
          >
            {loading === "REASON" ? <CircularProgress size="1.5em" /> : "REASON"}
          </Button>
        </ReasonMenu>
      </div>
    )
  }
  else {
    return (
      <Box sx={processedStyle}>
        {props.row.swarmResolutionTypeId === BuyRequestStatus.Accepted
          ? "APPROVED"
          : props.row.swarmResolutionTypeId === BuyRequestStatus.Rejected
          ? "REJECTED"
          : props.row.swarmResolutionTypeId === BuyRequestStatus.Completed
          ? "COMPLETED"
          : props.row.swarmResolutionTypeId === BuyRequestStatus.Lost
          ? "LOST"
          : props.row.swarmResolutionTypeId === BuyRequestStatus.OverTheLimit
          ? "OVER THE LIMIT"
          : "CANCELLED"}
      </Box>
    )
  }
};

export default ActionButtonsCell;
